import { pixelRatio2d } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';
import {
  BufferGeometry,
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  NormalBufferAttributes,
  Shape,
  ShapeGeometry
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { globalStores } from '@/editor/stores';
import {
  ENTITY_PRIORITY,
  ENTITY_RENDER_ORDER,
  defaultOutlineColor,
  errorHighlightedColor,
  hoverOutlineColor,
  selectedOutlineColor,
  windowBgColor,
  doorBgOutlineColor
} from '@/editor/constant/entity';
import { EEntityStatus } from '../../types/entity';
import { disposeAllChildren, disposeAllChildrenThenRemoveFromParent, mirrorObject } from '../utils';
import { EDoorWindowType, IWindowData, IWindowRenderParams } from '@/editor/data/model/model';
import HighlightedLine from './highlightedLine';
import { EContainerType } from '@/editor/types/entity';
import { EEntityType } from '@/editor/types';
import { LineMaterial } from 'three/examples/jsm/Addons.js';
import BaseModel2d from './BaseModel2d';
import {
  getDoorArcOutline,
  getDoorWindowBgOutline,
  getDoorWindowSpecificOutline
} from '@/editor/data/model/doorWindowUtils';

class WindowModel extends BaseModel2d {
  private _id: string;
  private _status: EEntityStatus = EEntityStatus.normal;
  private _selected: boolean = false;
  private _type: EDoorWindowType = EDoorWindowType.window;

  private _width: number = 0;
  private _thickness: number = 0;
  private _center: IPoint2d = { x: 0, y: 0 };
  private _rotation: number = 0;
  private _flip: boolean = false;
  private _isEntrance: boolean = false;

  private _specificOutlineMaterial: LineMaterial;
  private _dashOutlineMaterial: LineMaterial;
  private _bgOutlineMaterial: LineMaterial;
  private _fanOutlineMaterial: LineMaterial;

  rendered = false;

  static subModelPositionZ = {
    other: 0.4,
    outline: 0.5,
    bg: 0.6,
    bgOutline: 0.7,
    fan: 0.8,
    fanOutline: 0.9
  };

  constructor(params: Pick<IWindowData, 'id' | 'type'>) {
    super();
    this._id = params.id;

    this._modelGroup.userData.type = EEntityType.DoorWindow;
    this._modelGroup.userData.priority = ENTITY_PRIORITY.doorWindow;
    this._modelGroup.userData.entityId = this._id;
    this._modelGroup.userData.containerType = EContainerType.entity;
    this._modelGroup.userData.flip = false;

    this._specificOutlineMaterial = new LineMaterial({
      color: defaultOutlineColor,
      linewidth: 0.5,
      side: DoubleSide
    });

    this._dashOutlineMaterial = new LineMaterial({
      color: defaultOutlineColor,
      linewidth: 1, // 宽度（单位：像素）
      dashed: true, // 启用虚线
      dashScale: 10, // 虚线缩放因子
      dashSize: 0.5, // 虚线段的长度
      gapSize: 0.25, // 间隔的长度
      side: DoubleSide
    });

    this._bgOutlineMaterial = new LineMaterial({
      color: doorBgOutlineColor,
      linewidth: 0.5,
      side: DoubleSide
    });

    this._fanOutlineMaterial = new LineMaterial({
      color: doorBgOutlineColor,
      linewidth: 0.5,
      side: DoubleSide
    });

    this.setDisplayOrder(ENTITY_RENDER_ORDER.doorWindowBg);

    globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._modelGroup);
  }

  override setVisible(visible: boolean) {
    this._modelGroup.visible = visible;
  }

  update(params: IWindowRenderParams) {
    this._width = params.width;
    this._thickness = params.thickness;
    this._center = params.center;
    this._rotation = params.rotation;
    this._flip = params.flip;
    this._status = params.status;
    this._selected = params.selected;
    this._isEntrance = params.isEntrance;
    this._type = params.type;

    this.clear();
    this.createModel();

    if (params.flip) {
      mirrorObject(this._modelGroup);
    }

    this.updateWithoutDestroy({
      status: this._status,
      selected: this._selected,
      center: this._center,
      rotation: this._rotation,
      isEntrance: this._isEntrance
    });
  }

  updateWithoutDestroy({
    status,
    selected,
    center,
    rotation,
    isEntrance
  }: {
    status?: EEntityStatus;
    selected?: boolean;
    center?: IPoint2d;
    rotation?: number;
    isEntrance?: boolean;
  }) {
    this._status = status || EEntityStatus.normal;
    this._selected = selected || false;

    let color = defaultOutlineColor;
    let bgColor = doorBgOutlineColor;
    let lineWidth = 0.5;
    if (isEntrance) {
      color = 0xf28600;
      bgColor = 0xf28600;
    } else {
      if (this._selected) {
        color = selectedOutlineColor;
        bgColor = selectedOutlineColor;
        lineWidth = 1;
      } else {
        switch (this._status) {
          case EEntityStatus.normal:
            color = defaultOutlineColor;
            bgColor = doorBgOutlineColor;
            break;
          case EEntityStatus.hover:
            color = hoverOutlineColor;
            bgColor = hoverOutlineColor;
            lineWidth = 1;
            break;
          case EEntityStatus.unnormal:
            color = errorHighlightedColor;
            bgColor = errorHighlightedColor;
            break;
          default:
            break;
        }
      }
    }
    this._specificOutlineMaterial.color = new Color(color);
    this._specificOutlineMaterial.linewidth = lineWidth;
    this._dashOutlineMaterial.color = new Color(color);
    this._dashOutlineMaterial.linewidth = lineWidth;
    this._bgOutlineMaterial.color = new Color(bgColor);
    this._bgOutlineMaterial.linewidth = lineWidth;
    this._fanOutlineMaterial.color = new Color(bgColor);

    if (center) {
      this._modelGroup.position.setX(center.x / pixelRatio2d);
      this._modelGroup.position.setY(center.y / pixelRatio2d);
    }

    if (typeof rotation === 'number') {
      this._modelGroup.rotation.z = rotation;
    }
  }

  private createModel() {
    this.createModelBg();
    this.createModelOutline();
    this.rendered = true;
  }

  private createModelBg() {
    {
      const bgGeometry = WindowModel.createDoorWindowBgGeometry(this._width, this._thickness);
      const bgMesh = new Mesh(bgGeometry, new MeshBasicMaterial({ color: windowBgColor, side: DoubleSide }));
      bgMesh.position.z = WindowModel.subModelPositionZ.bg;
      this._modelGroup.add(bgMesh);
    }

    // ------------------------------

    const { otherGeometry, fanGeometry } = WindowModel.createDoorWindowSpecificGeometry(this._type, this._width);
    const otherMesh = new Mesh(
      otherGeometry,
      new MeshBasicMaterial({
        color: windowBgColor,
        transparent: true,
        opacity: 0.6,
        side: DoubleSide
      })
    );
    otherMesh.position.z = WindowModel.subModelPositionZ.other;
    this._modelGroup.add(otherMesh);

    // ------------------------------

    const fanMesh = new Mesh(fanGeometry, new MeshBasicMaterial({ color: windowBgColor, side: DoubleSide }));
    fanMesh.position.z = WindowModel.subModelPositionZ.fan;
    this._modelGroup.add(fanMesh);
  }

  private createModelOutline() {
    let outlineGroup;
    switch (this._type) {
      case EDoorWindowType.window:
      case EDoorWindowType.frenchWindow:
        outlineGroup = this.createBgOutlineGroup(this._width, this._thickness);
        outlineGroup.position.z = WindowModel.subModelPositionZ.bgOutline;
        this._modelGroup.add(outlineGroup);
        break;
      case EDoorWindowType.singleDoor:
      case EDoorWindowType.doubleDoor:
      case EDoorWindowType.motherDoor:
        outlineGroup = this.createBgOutlineGroup(this._width, this._thickness);
        outlineGroup.position.z = WindowModel.subModelPositionZ.outline;
        this._modelGroup.add(outlineGroup);
        outlineGroup = this.createDoorOutlineGroup(this._type, this._width);
        outlineGroup.position.z = WindowModel.subModelPositionZ.outline;
        this._modelGroup.add(outlineGroup);
        outlineGroup = this.createFanOutlineGroup(this._type, this._width);
        outlineGroup.position.z = WindowModel.subModelPositionZ.fanOutline;
        this._modelGroup.add(outlineGroup);
        break;
      default:
        break;
    }
  }

  private createFanOutlineGroup(type: EDoorWindowType, width: number) {
    const shapeLineGroup = new Group();

    const specificOutline = getDoorWindowSpecificOutline(type, width);
    for (const outline of specificOutline) {
      const len = outline.length;
      for (let i = 0; i < len; i++) {
        const start = outline[i];
        const end = outline[(i + 1) % len];
        const highlightedLine = new HighlightedLine([start, end], ENTITY_RENDER_ORDER.doorWindowFanOutline, 1);

        highlightedLine.setMaterial(this._specificOutlineMaterial);
        shapeLineGroup.add(highlightedLine.highlightedLine);
      }
    }

    return shapeLineGroup;
  }
  private createDoorOutlineGroup(type: EDoorWindowType, width: number) {
    const shapeLineGroup = new Group();

    const arcOutlines = this.createArcOutline(type, width);
    arcOutlines.forEach((arcLine) => shapeLineGroup.add(arcLine.highlightedLine));

    return shapeLineGroup;
  }

  private createArcOutline(type: EDoorWindowType, width: number) {
    const arcOutlines: HighlightedLine[] = [];

    const arcOutline = getDoorArcOutline(type, width);
    for (const outline of arcOutline) {
      const highlightedLine = new HighlightedLine(outline, ENTITY_RENDER_ORDER.columnOutline);
      highlightedLine.setMaterial(this._fanOutlineMaterial);
      arcOutlines.push(highlightedLine);
    }

    return arcOutlines;
  }

  private createBgOutlineGroup(width: number, thickness: number) {
    const shapeLineGroup = new Group();

    const material =
      this._type === EDoorWindowType.window || this._type === EDoorWindowType.frenchWindow
        ? this._specificOutlineMaterial
        : this._bgOutlineMaterial;

    const outline = getDoorWindowBgOutline(width, thickness);
    const len = outline.length;
    for (let i = 0; i < len; i++) {
      const start = outline[i];
      const end = outline[(i + 1) % len];
      const highlightedLine = new HighlightedLine([start, end], ENTITY_RENDER_ORDER.doorWindowBgOutline, 1);

      highlightedLine.setMaterial(material);

      shapeLineGroup.add(highlightedLine.highlightedLine);
    }

    if (this._type === EDoorWindowType.window || this._type === EDoorWindowType.frenchWindow) {
      const middleHighlightedLine = new HighlightedLine(
        [
          { x: -width / 2, y: 0 },
          { x: width / 2, y: 0 }
        ],
        ENTITY_RENDER_ORDER.doorWindowBgOutline,
        1
      );
      middleHighlightedLine.setMaterial(
        this._type === EDoorWindowType.frenchWindow ? this._dashOutlineMaterial : material
      );

      if (this._type === EDoorWindowType.frenchWindow) {
        middleHighlightedLine.highlightedLine.computeLineDistances(); // 计算线条距离，对虚线很重要;
      }
      shapeLineGroup.add(middleHighlightedLine.highlightedLine);
    }

    return shapeLineGroup;
  }

  static createDoorWindowBgGeometry(width: number, thickness: number) {
    const outline = getDoorWindowBgOutline(width, thickness);
    const heartShape = new Shape();
    const len = outline.length;
    for (let i = 0; i < len; i++) {
      if (i === 0) {
        heartShape.moveTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
      }
      heartShape.lineTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
    }
    const geometry = new ShapeGeometry(heartShape);

    return geometry;
  }

  static createDoorWindowSpecificGeometry(type: EDoorWindowType, width: number) {
    const { otherGeometries, fanGeometries } = WindowModel.createSpecificDoorWindowGeometry(type, width);
    let otherGeometry: BufferGeometry<NormalBufferAttributes> | undefined;
    let fanGeometry: BufferGeometry<NormalBufferAttributes> | undefined;

    // 合并成一个 BufferGeometry
    if (otherGeometries.length) {
      otherGeometry = mergeGeometries(otherGeometries);
    }

    if (fanGeometries.length) {
      fanGeometry = mergeGeometries(fanGeometries);
    }
    return { otherGeometry, fanGeometry };
  }

  static createSingleDoorSpecificGeometry(type: EDoorWindowType, width: number) {
    const otherGeometries: ShapeGeometry[] = [];
    const fanGeometries: ShapeGeometry[] = [];

    const specificOutline = getDoorWindowSpecificOutline(type, width);
    for (const outline of specificOutline) {
      const rightShape = new Shape();
      const len = outline.length;
      for (let i = 0; i < len; i++) {
        if (i === 0) {
          rightShape.moveTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
        }
        rightShape.lineTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
      }
      const geometry = new ShapeGeometry(rightShape);
      fanGeometries.push(geometry);
    }

    const arcShape = new Shape();
    arcShape.moveTo(width / 2 / pixelRatio2d, 0);
    arcShape.lineTo(-width / 2 / pixelRatio2d, 0);
    // 绘制四分之一圆弧
    arcShape.absarc(width / 2 / pixelRatio2d, 0, width / pixelRatio2d, Math.PI, Math.PI / 2, true);
    // 闭合路径回到原点
    arcShape.lineTo(width / 2 / pixelRatio2d, 0);

    // 创建几何体和材质
    const arcGeometry = new ShapeGeometry(arcShape);
    otherGeometries.push(arcGeometry);

    return { otherGeometries, fanGeometries };
  }

  static createDoubleDoorSpecificGeometry(type: EDoorWindowType, width: number) {
    const otherGeometries: ShapeGeometry[] = [];
    const fanGeometries: ShapeGeometry[] = [];

    const specificOutline = getDoorWindowSpecificOutline(type, width);
    for (const outline of specificOutline) {
      const sideShape = new Shape();
      const len = outline.length;
      for (let i = 0; i < len; i++) {
        if (i === 0) {
          sideShape.moveTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
        }
        sideShape.lineTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
      }
      const geometry = new ShapeGeometry(sideShape);
      fanGeometries.push(geometry);
    }

    const radius = width / 2 / pixelRatio2d;

    const leftArcShape = new Shape();
    leftArcShape.moveTo(-radius, 0);
    leftArcShape.lineTo(0, 0);
    // 绘制四分之一圆弧
    leftArcShape.absarc(-radius, 0, radius, 0, Math.PI / 2, false);
    // 闭合路径回到原点
    leftArcShape.lineTo(-radius, 0);

    // 创建几何体和材质
    const leftArcGeometry = new ShapeGeometry(leftArcShape);
    otherGeometries.push(leftArcGeometry);

    const rightArcShape = new Shape();
    rightArcShape.moveTo(radius, 0);
    rightArcShape.lineTo(0, 0);
    // 绘制四分之一圆弧
    rightArcShape.absarc(radius, 0, radius, Math.PI, Math.PI / 2, true);
    // 闭合路径回到原点
    rightArcShape.lineTo(radius, 0);

    // 创建几何体和材质
    const rightArcGeometry = new ShapeGeometry(rightArcShape);
    otherGeometries.push(rightArcGeometry);

    return { otherGeometries, fanGeometries };
  }

  static createMotherDoorSpecificGeometry(type: EDoorWindowType, width: number) {
    const otherGeometries: ShapeGeometry[] = [];
    const fanGeometries: ShapeGeometry[] = [];

    const specificOutline = getDoorWindowSpecificOutline(type, width);
    for (const outline of specificOutline) {
      const sideShape = new Shape();
      const len = outline.length;
      for (let i = 0; i < len; i++) {
        if (i === 0) {
          sideShape.moveTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
        }
        sideShape.lineTo(outline[i].x / pixelRatio2d, outline[i].y / pixelRatio2d);
      }
      const geometry = new ShapeGeometry(sideShape);
      fanGeometries.push(geometry);
    }

    const leftRadius = (width * 0.3) / pixelRatio2d;
    const leftArcShape = new Shape();
    leftArcShape.moveTo(-(width * 0.5) / pixelRatio2d, 0);
    leftArcShape.lineTo((-width * 0.2) / pixelRatio2d, 0);
    // 绘制四分之一圆弧
    leftArcShape.absarc(-(width * 0.5) / pixelRatio2d, 0, leftRadius, 0, Math.PI / 2, false);
    // 闭合路径回到原点
    leftArcShape.lineTo(-(width * 0.5) / pixelRatio2d, 0);
    // 创建几何体和材质
    const leftArcGeometry = new ShapeGeometry(leftArcShape);
    otherGeometries.push(leftArcGeometry);

    const rightRadius = (width * 0.7) / pixelRatio2d;
    const rightArcShape = new Shape();
    rightArcShape.moveTo((width * 0.5) / pixelRatio2d, 0);
    rightArcShape.lineTo((-width * 0.2) / pixelRatio2d, 0);
    // 绘制四分之一圆弧
    rightArcShape.absarc((width * 0.5) / pixelRatio2d, 0, rightRadius, Math.PI, Math.PI / 2, true);
    // 闭合路径回到原点
    rightArcShape.lineTo((width * 0.5) / pixelRatio2d, 0);
    // 创建几何体和材质
    const rightArcGeometry = new ShapeGeometry(rightArcShape);
    otherGeometries.push(rightArcGeometry);

    return { otherGeometries, fanGeometries };
  }

  static createSpecificDoorWindowGeometry(type: EDoorWindowType, width: number) {
    let otherGeometries: ShapeGeometry[] = [];
    let fanGeometries: ShapeGeometry[] = [];
    switch (type) {
      case EDoorWindowType.singleDoor:
        ({ otherGeometries, fanGeometries } = WindowModel.createSingleDoorSpecificGeometry(type, width));
        break;
      case EDoorWindowType.doubleDoor:
        ({ otherGeometries, fanGeometries } = WindowModel.createDoubleDoorSpecificGeometry(type, width));
        break;
      case EDoorWindowType.motherDoor:
        ({ otherGeometries, fanGeometries } = WindowModel.createMotherDoorSpecificGeometry(type, width));
        break;
      default:
        break;
    }

    return { otherGeometries, fanGeometries };
  }

  override clear() {
    disposeAllChildren(this._modelGroup);
    this.rendered = false;
  }

  override dispose() {
    disposeAllChildrenThenRemoveFromParent(this._modelGroup);
    this.rendered = false;
  }
}

export default WindowModel;
